/**
 * Stripe Client Setup for HajiPay
 * Initializes Stripe.js with white-label configuration
 * Handles client-side Stripe operations
 */

import { loadStripe, Stripe, StripeElements, StripeElement, StripeError } from '@stripe/stripe-js';
import { STRIPE_CONFIG, validateStripeConfig } from './config';

// Re-export config for use in hooks
export { STRIPE_CONFIG } from './config';

/**
 * Global Stripe instance
 * Lazy-loaded to prevent unnecessary bundle size
 */
let stripePromise: Promise<Stripe | null> | null = null;

/**
 * Load Stripe.js with configuration
 */
export const loadStripeInstance = async (): Promise<Stripe | null> => {
  if (!stripePromise) {
    try {
      // Validate configuration before loading
      validateStripeConfig();

      stripePromise = loadStripe(STRIPE_CONFIG.publishableKey, {
        locale: 'auto',
      });
    } catch (error) {
      console.error('❌ Stripe configuration error:', error);
      stripePromise = Promise.resolve(null);
    }
  }

  return stripePromise;
};

/**
 * Get Stripe instance (cached)
 */
export const getStripeInstance = (): Promise<Stripe | null> => {
  return loadStripeInstance();
};

/**
 * Create Payment Element
 */
export const createPaymentElement = async (
  elements: StripeElements,
  options?: any
): Promise<StripeElement | null> => {
  try {
    const paymentElement = elements.create('payment', {
      ...STRIPE_CONFIG.paymentElementOptions,
      ...options,
    });

    return paymentElement;
  } catch (error) {
    console.error('❌ Failed to create payment element:', error);
    return null;
  }
};

/**
 * Create Address Element
 */
export const createAddressElement = async (
  elements: StripeElements,
  options?: any
): Promise<StripeElement | null> => {
  try {
    const addressElement = elements.create('address', {
      mode: 'billing',
      ...options,
    });

    return addressElement;
  } catch (error) {
    console.error('❌ Failed to create address element:', error);
    return null;
  }
};

/**
 * Confirm Payment Intent
 */
export const confirmPaymentIntent = async (
  stripe: Stripe,
  elements: StripeElements,
  options: {
    clientSecret: string;
    returnUrl?: string;
    confirmParams?: any;
  }
): Promise<{ error?: any; paymentIntent?: any }> => {
  try {
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      clientSecret: options.clientSecret,
      confirmParams: {
        return_url: options.returnUrl || `${STRIPE_CONFIG.appBaseUrl}/donate/success`,
        ...options.confirmParams,
      },
      redirect: 'if_required',
    });

    if (error) {
      console.error('❌ Payment confirmation failed:', error);
      return { error };
    }

    console.log('✅ Payment confirmed:', paymentIntent);
    return { paymentIntent };
  } catch (error) {
    console.error('❌ Payment confirmation error:', error);
    return { error };
  }
};

/**
 * Confirm Setup Intent (for saving cards)
 */
export const confirmSetupIntent = async (
  stripe: Stripe,
  elements: StripeElements,
  options: {
    clientSecret: string;
    returnUrl?: string;
    confirmParams?: any;
  }
): Promise<{ error?: any; setupIntent?: any }> => {
  try {
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      clientSecret: options.clientSecret,
      confirmParams: {
        return_url: options.returnUrl || `${STRIPE_CONFIG.appBaseUrl}/account/billing`,
        ...options.confirmParams,
      },
      redirect: 'if_required',
    });

    if (error) {
      console.error('❌ Setup confirmation failed:', error);
      return { error };
    }

    console.log('✅ Setup confirmed:', setupIntent);
    return { setupIntent };
  } catch (error) {
    console.error('❌ Setup confirmation error:', error);
    return { error };
  }
};

/**
 * Retrieve Payment Intent status
 * Note: This should be called from server-side as client-side doesn't have paymentIntents API
 */
export const retrievePaymentIntent = async (
  _paymentIntentId: string
): Promise<any> => {
  try {
    // This would typically be called server-side
    // For client-side, we'd use the payment intent from confirmPayment result
    console.warn('⚠️ retrievePaymentIntent called client-side - consider server-side implementation');
    throw new Error('Payment intent retrieval should be done server-side');
  } catch (error) {
    console.error('❌ Failed to retrieve payment intent:', error);
    throw error;
  }
};

/**
 * Retrieve Setup Intent status
 * Note: This should be called from server-side as client-side doesn't have setupIntents API
 */
export const retrieveSetupIntent = async (
  _setupIntentId: string
): Promise<any> => {
  try {
    // This would typically be called server-side
    // For client-side, we'd use the setup intent from confirmSetup result
    console.warn('⚠️ retrieveSetupIntent called client-side - consider server-side implementation');
    throw new Error('Setup intent retrieval should be done server-side');
  } catch (error) {
    console.error('❌ Failed to retrieve setup intent:', error);
    throw error;
  }
};

/**
 * Handle Stripe errors with user-friendly messages
 */
export const handleStripeError = (error: StripeError | Error | any): { code: string; message: string; type: string } => {
  console.error('🔴 Stripe Error:', error);

  // Card errors
  if (error.type === 'card_error') {
    return {
      code: error.code || 'card_error',
      message: error.message || 'Your card was declined. Please try a different card.',
      type: 'card_error',
    };
  }

  // Validation errors
  if (error.type === 'validation_error') {
    return {
      code: error.code || 'validation_error',
      message: error.message || 'Please check your payment information.',
      type: 'validation_error',
    };
  }

  // Network errors
  if (error.type === 'network_error' || error.message?.includes('network')) {
    return {
      code: 'network_error',
      message: 'Network error. Please check your connection and try again.',
      type: 'network_error',
    };
  }

  // Generic error
  return {
    code: error.code || 'unknown_error',
    message: error.message || 'An unexpected error occurred. Please try again.',
    type: error.type || 'unknown',
  };
};

/**
 * Check if Stripe is available
 */
export const isStripeAvailable = async (): Promise<boolean> => {
  try {
    const stripe = await getStripeInstance();
    return stripe !== null;
  } catch {
    return false;
  }
};

/**
 * Preload Stripe.js for better performance
 */
export const preloadStripe = (): void => {
  if (typeof window !== 'undefined') {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = 'https://js.stripe.com/v3/';
    link.as = 'script';
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }
};

/**
 * Initialize Stripe on app startup
 */
export const initializeStripe = (): void => {
  // Preload Stripe.js
  preloadStripe();

  // Validate configuration
  try {
    validateStripeConfig();
    console.log('✅ Stripe initialized successfully');
  } catch (error) {
    console.error('❌ Stripe initialization failed:', error);
  }
};
